/* eslint-disable no-unused-vars */
import _ from 'underscore';
import URI from 'urijs';
import type {
  IQueryParam,
  IQueryParams,
  IRouteLevel,
  IRouteState,
} from './types';

let lastHash: string;

const specificDigitsParams = {
  accountId: 'aid',
  customerId: 'cid',
  id: 'id',
  userId: 'uid',
};

const specificDigitsParamsValues = _.values(specificDigitsParams);

const isDigits = (s: string): boolean => /^\d+$/.test(s);
const buildQuery = URI.buildQuery as (data: object, duplicateQueryParameters: boolean, escapeQuerySpace: boolean) => string;

const mapSpecificsParams = (params: IQueryParams): Record<string, IQueryParam | undefined> => _.mapObject(params, (value: IQueryParam, key: string) => {
  if (_.contains(specificDigitsParamsValues, key) && _.isArray(value)) {
    // In case of an array of params values is returns,
    // we return the 1st digit value or undefined if none
    return _.find(value, isDigits);
  }
  return value;
});

/**
 * This class is a wrapper of HTML5 History API and stores no data itself
 */
export class Router {
  /**
   * An alias of window.history.back();
   * @returns {undefined} - perform window.history.back()
   */
  static back(): void {
    window.history.back();
  }

  /**
   * An alias of window.history.forward();
   * @returns {undefined} - perform window.history.forward()
   */
  static forward(): void {
    window.history.forward();
  }

  /**
   * A wrapper of window.history.go();
   * @param {number|string} numberOrUrl - (required) The parameter can either be a number which
   *                                                   goes to the URL within the specific
   *                                                   position (-1 goes back one page, 1 goes
   *                                                   forward one page),or a string.The string
   *                                                   must be a partial or full URL,and the
   *                                                   function will go to the first URL that
   *                                                   matches the string.
   * @returns {undefined} - perform window.history.go()
   */
  static go(numberOrUrl: number | string): void {
    window.history.go(numberOrUrl as any);
  }

  /**
   * Convert route state to URL hash
   * @param {object} state - (required) the route state need to be converted
   *
   * @returns {string} the URL hash string.
   */
  static formatHash(state: IRouteState): string {
    const generateHashForEachLevel = (level: IRouteLevel): string => {
      const paramsString = _.isEmpty(_.compact(level.parameters)) ? ''
        : `(${_.map(level.parameters, encodeURIComponent).join(',')})`;

      return `/${encodeURIComponent(level.name)}${paramsString}`;
    };

    const path = _.isEmpty(state.levels) ? '/'
      : _.map(state.levels, generateHashForEachLevel).join('');

    const query = _.isEmpty(state.properties) ? ''
      : `?${buildQuery(state.properties, false, false)}`;

    return `#${path}${query}`;
  }

  /**
   * Convert URL hash to route state
   * @param {string} hash - (required) the hash need to be converted
   *
   * @returns {object} the route state
   */
  static parseHash(hash: string): IRouteState {
    const [, path, query] = /^#([^?]+)(?:\?([^?]*))?$/.exec(hash)!;
    const levels = _.chain((path || '').split('/'))
      .reject(_.isEmpty)
      .map((level: string) => {
        const res = /^([^(]+)(?:\(([^)]*)\))?$/.exec(level);
        return {
          name: decodeURIComponent(res![1]),
          parameters: _.map(
            decodeURIComponent(res![2] || '').split(','),
            (param: string) => param.trim()
          ),
        };
      }).value();

    const properties = URI.parseQuery(query) as IQueryParams;

    return {
      levels,
      properties,
    };
  }

  /**
   * Convert URL path and query to route state
   * @param {string} url - (required) the url need to be converted
   *
   * @returns {object} the route state
   */
  static parsePathAndQuery(url: string): IRouteState {
    const uri = new URI(url);
    const params = URI.parseQuery(uri.query()) as IQueryParams;
    const levels = _.map(uri.segment(), (level: string) => _.object(['name', 'parameters'], [decodeURIComponent(level), null])) as unknown as IRouteLevel[];

    return {
      levels,
      properties: mapSpecificsParams(params),
    };
  }

  /**
   * Perform a navigation based on the new route state
   * @param {object|string} route - (required) The parameter can either be a route state
   *                                or a URL hash string
   * @returns {undefined} - perform URL change
   */
  static navigate(route: IRouteState | string): void {
    if (_.isObject(route)) {
      window.location.hash = this.formatHash(route as IRouteState);
    } else {
      window.location.href = route as string;
    }
  }

  /**
   * Calls the callback with a route state object on navigation
   * @param {function} callback - (required) The callback function for navigation
   * @returns {undefined} - listen to event and perform callback
   */
  static onNavigate(callback: (state: IRouteState) => void): void {
    const navigationHandler = (): void => {
      if (lastHash !== window.location.hash) {
        lastHash = window.location.hash;
        callback(this.parseHash(lastHash));
      }
    };

    // Polyfill for IE 11
    window.addEventListener('popstate', navigationHandler);
    window.addEventListener('hashchange', navigationHandler);
  }
}
